import styled from 'styled-components/macro'
import {
  commonModalContentStyle,
  gradientModalStyle,
  gradientModalHeaderStyle,
  gradientModalScrollbarStyle,
  blueGradient,
  squircle,
} from 'src/global/helpers'

export const Modal = styled.div`
  ${props => gradientModalStyle(blueGradient, props.theme.blue, 'translateY(-50%) translate(0, 12vh) scale(0.01)')}
`

export const Header = styled.div`
  ${gradientModalHeaderStyle}
`

export const ModalContent = styled.div`
  ${commonModalContentStyle}
  ${gradientModalScrollbarStyle}
  padding: 0 24px 120px;

  .subscription-warning {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 4px 0;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.38);
    ${squircle(16)}
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease;

    svg {
      flex-shrink: 0;
      font-size: 15px;
      color: #ffd27a;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.55);
    }
  }
`

export const Section = styled.section`
  margin-bottom: 32px;
  padding: 0 4px;

  &.intro {
    padding: 0 4px 4px;
    margin-bottom: 28px;
  }

  &.next-steps {
    padding: 4px 4px 0;
    margin-top: -8px;
    margin-bottom: 0;

    p {
      margin: 0 0 16px;
      font-size: 14px;
      color: #ffffff;
      line-height: 1.5;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }

  &:last-child {
    margin-bottom: 0;
  }

  &.bonus {
    padding: 14px 16px 16px;
    background: rgba(255, 255, 255, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.42);
    ${squircle(20)}
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);

    .config-note {
      margin: 16px 0 0;
    }
  }

  h3 {
    margin: 8px 0 10px;
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.3;
    text-align: left;
  }

  p {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.92);
    text-align: left;
  }

  &.intro p {
    font-size: 15px;
    margin: 0;
  }

  p.hint {
    margin: -4px 0 12px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.78);
    font-style: italic;
  }

  .bonus-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;

    h3 {
      margin: 0;
    }
  }

  .free-tag {
    padding: 3px 8px;
    ${squircle(20, 20)}
    background: rgba(255, 255, 255, 0.32);
    border: 1px solid rgba(255, 255, 255, 0.5);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.4px;
    color: #ffffff;
  }

  .benefits {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .benefits li {
    position: relative;
    padding: 6px 0 6px 22px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.95);

    &::before {
      content: '';
      position: absolute;
      left: 4px;
      top: 14px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #ffffff;
    }
  }
`

type OptionTileProps = { $active: boolean }

export const OptionTile = styled.div<OptionTileProps>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
  ${squircle(16)}
  background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.22)' : 'rgba(255, 255, 255, 0.12)')};
  border: 1px solid ${props => (props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.22)')};
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: ${props => (props.$active ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.18)')};
  }

  .check {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    border-radius: 50%;
    background: ${props => (props.$active ? '#ffffff' : 'transparent')};
    border: 2px solid ${props => (props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.55)')};
    flex-shrink: 0;
    position: relative;
    transition: background 0.18s ease, border-color 0.18s ease;

    ${props =>
      props.$active &&
      `
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 6px;
        height: 6px;
        background: #11b7f3;
        border-radius: 50%;
        transform: translate(-50%, -50%);
      }
    `}
  }

  .content {
    flex: 1;
  }

  .option-title {
    font-size: 14px;
    font-weight: ${props => (props.$active ? 700 : 500)};
    color: #ffffff;
    margin-bottom: 2px;
  }

  .description {
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.82);
    line-height: 1.4;
  }
`

export const SizeButtons = styled.div`
  display: flex;
  gap: 8px;

  button {
    flex: 1;
    padding: 12px 8px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    ${squircle(16)}
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.18);
    }

    &.active {
      background: rgba(255, 255, 255, 0.23);
      border-color: #ffffff;
      font-weight: 700;
    }
  }
`

export const Footer = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 14px 20px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  ${squircle(24)}
  backdrop-filter: blur(17px);
  -webkit-backdrop-filter: blur(17px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  z-index: 2;

  .cta {
    padding: 12px 20px;
    background: #ffffff;
    color: #11b7f3;
    border: none;
    ${squircle(40)}
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      box-shadow: none;
    }

    &:disabled:hover {
      transform: none;
      box-shadow: none;
    }
  }
`

export const TotalDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.82);
  }

  .amount {
    font-size: 22px;
    font-weight: 800;
    color: #ffffff;
    line-height: 1;
  }
`
